import { createContext, useContext, type ReactNode } from 'react'

import { cn } from './cn'

const LightScope = createContext(false)

/**
 * A subtree that stays light whatever the viewer's theme is: the Customer and
 * Public areas, whose editorial white is part of the design rather than a
 * theme setting.
 *
 * The class alone would not carry: a dropdown, a tooltip and a dialog all
 * render through a portal under `<body>`, where the palette this class
 * declares is out of scope. The context is what lets them find it again.
 *
 * The class carries the ground and the ink as well as the palette, so a page
 * that leaves a gap shows the editorial surface behind it rather than the app
 * frame — which does follow the theme. A page that wants a different ground
 * passes one; a utility wins over the rule.
 */
export function LightThemeScope({
  className,
  children,
}: {
  className?: string
  children?: ReactNode
}) {
  return (
    <LightScope.Provider value={true}>
      <div className={cn('theme-light', className)}>{children}</div>
    </LightScope.Provider>
  )
}

/** The theme class a portalled surface has to repeat, because the portal
 *  escapes the element that set it. */
export function usePortalThemeClass() {
  return useContext(LightScope) ? 'theme-light' : undefined
}

/**
 * True inside Customer and Public, false inside Internal.
 *
 * The two areas are wrapped by `LightThemeScope` and Internal is not, so a
 * cross-area component can pick the right button — navy `Button` or editorial
 * `CtaButton` — without every call site restating which area it is on.
 */
export function useEditorialSurface() {
  return useContext(LightScope)
}
